import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { formatarPreco } from '../utils/priceUtils';

const CommissionRules = ({ salonId, professionals, services }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRule, setShowAddRule] = useState(false);
  const [activeTab, setActiveTab] = useState('simple'); // 'simple', 'service', 'advanced'
  
  // Novo estado para regra sendo criada
  const [newRule, setNewRule] = useState({
    name: '',
    type: 'simple',
    professionalId: 'all',
    commissionPercentage: 30, // Valor padrão de 30%
    serviceRules: [], // Para regras por serviço
    advancedConditions: [], // Para regras avançadas
    active: true
  });

  // Carrega regras existentes
  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        const rulesRef = collection(db, `salons/${salonId}/commissionRules`);
        const rulesSnapshot = await getDocs(rulesRef);
        const rulesData = rulesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setRules(rulesData);
      } catch (error) {
        console.error('Erro ao carregar regras de comissão:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (salonId) {
      fetchRules();
    }
  }, [salonId]);

  // Preparar regras por serviço
  useEffect(() => {
    if (services.length > 0 && newRule.serviceRules.length === 0) {
      // Inicializa todas as regras de serviço com 30% por padrão
      const initialServiceRules = services.map(service => ({
        serviceId: service.id,
        serviceName: service.nome,
        percentage: 30
      }));
      
      setNewRule(prev => ({
        ...prev,
        serviceRules: initialServiceRules
      }));
    }
  }, [services]);

  // Salvar nova regra
  const handleSaveRule = async () => {
    try {
      if (!newRule.name) {
        alert('Por favor, insira um nome para a regra');
        return;
      }
      
      const ruleData = {
        name: newRule.name,
        type: newRule.type,
        professionalId: newRule.professionalId,
        commissionPercentage: newRule.type === 'simple' ? newRule.commissionPercentage : null,
        serviceRules: newRule.type === 'service' ? newRule.serviceRules : [],
        advancedConditions: newRule.type === 'advanced' ? newRule.advancedConditions : [],
        active: true,
        createdAt: new Date()
      };
      
      const rulesRef = collection(db, `salons/${salonId}/commissionRules`);
      const docRef = await addDoc(rulesRef, ruleData);
      
      // Adicionar a nova regra à lista
      setRules(prev => [...prev, { id: docRef.id, ...ruleData }]);
      
      // Resetar formulário
      setNewRule({
        name: '',
        type: 'simple',
        professionalId: 'all',
        commissionPercentage: 30,
        serviceRules: services.map(service => ({
          serviceId: service.id,
          serviceName: service.nome,
          percentage: 30
        })),
        advancedConditions: [],
        active: true
      });
      
      setShowAddRule(false);
    } catch (error) {
      console.error('Erro ao salvar regra:', error);
      alert('Erro ao salvar a regra de comissão. Por favor, tente novamente.');
    }
  };

  // Atualizar valores da nova regra
  const handleRuleChange = (e) => {
    const { name, value } = e.target;
    setNewRule(prev => ({
      ...prev,
      [name]: name === 'commissionPercentage' ? Number(value) : value
    }));
  };

  // Atualizar regra por serviço
  const handleServiceRuleChange = (serviceId, percentage) => {
    setNewRule(prev => ({
      ...prev,
      serviceRules: prev.serviceRules.map(rule => 
        rule.serviceId === serviceId ? { ...rule, percentage: Number(percentage) } : rule
      )
    }));
  };

  // Adicionar condição avançada
  const addAdvancedCondition = () => {
    const newCondition = {
      id: Date.now(),
      type: 'service', // 'service', 'value', 'professional'
      operator: '==', // '==', '>', '<', '>=', '<='
      value: '',
      serviceId: services.length > 0 ? services[0].id : '',
      professionalId: professionals.length > 0 ? professionals[0].id : '',
      valueThreshold: 0,
      percentage: 30
    };
    
    setNewRule(prev => ({
      ...prev,
      advancedConditions: [...prev.advancedConditions, newCondition]
    }));
  };

  // Remover condição avançada
  const removeAdvancedCondition = (conditionId) => {
    setNewRule(prev => ({
      ...prev,
      advancedConditions: prev.advancedConditions.filter(c => c.id !== conditionId)
    }));
  };

  // Atualizar condição avançada
  const updateAdvancedCondition = (conditionId, field, value) => {
    setNewRule(prev => ({
      ...prev,
      advancedConditions: prev.advancedConditions.map(condition => 
        condition.id === conditionId ? { ...condition, [field]: field === 'percentage' || field === 'valueThreshold' ? Number(value) : value } : condition
      )
    }));
  };

  // Ativar/Desativar regra
  const toggleRuleStatus = async (ruleId, currentStatus) => {
    try {
      const ruleRef = doc(db, `salons/${salonId}/commissionRules`, ruleId);
      await updateDoc(ruleRef, { active: !currentStatus });
      
      // Atualizar lista localmente
      setRules(prev => prev.map(rule => 
        rule.id === ruleId ? { ...rule, active: !currentStatus } : rule
      ));
    } catch (error) {
      console.error('Erro ao atualizar status da regra:', error);
      alert('Erro ao atualizar o status da regra.');
    }
  };

  // Excluir regra
  const deleteRule = async (ruleId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta regra de comissão?')) {
      return;
    }
    
    try {
      const ruleRef = doc(db, `salons/${salonId}/commissionRules`, ruleId);
      await deleteDoc(ruleRef);
      
      // Atualizar lista localmente
      setRules(prev => prev.filter(rule => rule.id !== ruleId));
    } catch (error) {
      console.error('Erro ao excluir regra:', error);
      alert('Erro ao excluir a regra de comissão.');
    }
  };

  // Tela de carregamento
  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <div className="animate-pulse text-gray-600">Carregando configurações de comissão...</div>
      </div>
    );
  }

  // Função para renderizar descrição da condição avançada
  const renderConditionDescription = (condition) => {
    if (condition.type === 'service') {
      const serviceName = services.find(s => s.id === condition.serviceId)?.nome || 'Serviço não encontrado';
      return `Se Serviço for ${serviceName} => ${condition.percentage}%`;
    } else if (condition.type === 'professional') {
      const professionalName = professionals.find(p => p.id === condition.professionalId)?.nome || 'Profissional não encontrado';
      return `Se Profissional for ${professionalName} => ${condition.percentage}%`;
    } else if (condition.type === 'value') {
      const operatorLabel = {
        '>': 'maior que',
        '<': 'menor que',
        '>=': 'maior ou igual a',
        '<=': 'menor ou igual a',
        '==': 'igual a'
      };
      return `Se Valor ${operatorLabel[condition.operator]} ${formatarPreco(condition.valueThreshold)} => ${condition.percentage}%`;
    }
    return 'Condição inválida';
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      {/* Cabeçalho */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Regras de Comissionamento
          </h3>
          <button
            onClick={() => setShowAddRule(!showAddRule)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            {showAddRule ? 'Cancelar' : '+ Nova Regra'}
          </button>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Configure como os profissionais recebem comissões pelos serviços realizados
        </p>
      </div>

      {/* Formulário para adicionar regra */}
      {showAddRule && (
        <div className="p-6 border-b border-gray-200 bg-gray-50 rounded-md m-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome da Regra
            </label>
            <input
              type="text"
              name="name"
              value={newRule.name}
              onChange={handleRuleChange}
              placeholder="Ex: Comissão padrão, Comissão para coloração, etc."
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          
          {/* Tabs para tipos de regra */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto">
              <button
                onClick={() => {
                  setActiveTab('simple');
                  setNewRule(prev => ({ ...prev, type: 'simple' }));
                }}
                className={`mr-4 py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'simple'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Regra Simples
              </button>
              <button
                onClick={() => {
                  setActiveTab('service');
                  setNewRule(prev => ({ ...prev, type: 'service' }));
                }}
                className={`mr-4 py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'service'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Por Serviço
              </button>
              <button
                onClick={() => {
                  setActiveTab('advanced');
                  setNewRule(prev => ({ ...prev, type: 'advanced' }));
                }}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'advanced'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Avançada
              </button>
            </nav>
          </div>
          
          {/* Seleção de profissional (para regras simples e por serviço) */}
          {(activeTab === 'simple' || activeTab === 'service') && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aplicar a
              </label>
              <select
                name="professionalId"
                value={newRule.professionalId}
                onChange={handleRuleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="all">Todos os profissionais</option>
                {professionals.map(pro => (
                  <option key={pro.id} value={pro.id}>{pro.nome}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Configuração baseada no tipo de regra */}
          {activeTab === 'simple' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Percentual de Comissão (%)
              </label>
              <div className="flex items-center">
                <input
                  type="range"
                  name="commissionPercentage"
                  min="0"
                  max="100"
                  value={newRule.commissionPercentage}
                  onChange={handleRuleChange}
                  className="w-full mr-3"
                />
                <div className="w-16 flex items-center">
                  <input
                    type="number"
                    name="commissionPercentage"
                    min="0"
                    max="100"
                    value={newRule.commissionPercentage}
                    onChange={handleRuleChange}
                    className="w-12 p-1 border border-gray-300 rounded-md text-center"
                    inputmode="numeric"
                  />
                  <span className="ml-1">%</span>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'service' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Percentual por Serviço
              </label>
              <div className="bg-white p-3 rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                {newRule.serviceRules.map((rule) => (
                  <div key={rule.serviceId} className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                    <span className="text-sm">{rule.serviceName}</span>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={rule.percentage}
                        onChange={(e) => handleServiceRuleChange(rule.serviceId, e.target.value)}
                        className="w-16 p-1 border border-gray-300 rounded-md text-center"
                        inputmode="numeric"
                      />
                      <span className="ml-1">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {activeTab === 'advanced' && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Regras Condicionais
                </label>
                <button
                  onClick={addAdvancedCondition}
                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                >
                  + Adicionar Condição
                </button>
              </div>
              
              <div className="bg-white p-3 rounded-md border border-gray-200">
                {newRule.advancedConditions.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    <p>Nenhuma condição definida</p>
                    <p className="text-xs mt-1">Adicione condições para criar regras personalizadas</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {newRule.advancedConditions.map((condition, index) => (
                      <div key={condition.id} className="p-3 border border-gray-200 rounded-md bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Condição {index + 1}</h4>
                          <button
                            onClick={() => removeAdvancedCondition(condition.id)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            Remover
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Tipo de Condição
                            </label>
                            <select
                              value={condition.type}
                              onChange={(e) => updateAdvancedCondition(condition.id, 'type', e.target.value)}
                              className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
                            >
                              <option value="service">Serviço específico</option>
                              <option value="professional">Profissional específico</option>
                              <option value="value">Valor do serviço</option>
                            </select>
                          </div>
                          
                          {condition.type === 'service' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Serviço
                              </label>
                              <select
                                value={condition.serviceId}
                                onChange={(e) => updateAdvancedCondition(condition.id, 'serviceId', e.target.value)}
                                className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
                              >
                                {services.map(service => (
                                  <option key={service.id} value={service.id}>{service.nome}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {condition.type === 'professional' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Profissional
                              </label>
                              <select
                                value={condition.professionalId}
                                onChange={(e) => updateAdvancedCondition(condition.id, 'professionalId', e.target.value)}
                                className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
                              >
                                {professionals.map(professional => (
                                  <option key={professional.id} value={professional.id}>{professional.nome}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {condition.type === 'value' && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                  Operador
                                </label>
                                <select
                                  value={condition.operator}
                                  onChange={(e) => updateAdvancedCondition(condition.id, 'operator', e.target.value)}
                                  className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
                                >
                                  <option value="==">=</option>
                                  <option value=">">{'>'}</option>
                                  <option value="<">{'<'}</option>
                                  <option value=">=">{'≥'}</option>
                                  <option value="<=">{'≤'}</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                  Valor (R$)
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={condition.valueThreshold}
                                  onChange={(e) => updateAdvancedCondition(condition.id, 'valueThreshold', e.target.value)}
                                  className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
                                  inputmode="decimal"
                                />
                              </div>
                            </>
                          )}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Percentual de Comissão (%)
                          </label>
                          <div className="flex items-center">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={condition.percentage}
                              onChange={(e) => updateAdvancedCondition(condition.id, 'percentage', e.target.value)}
                              className="w-full mr-3"
                            />
                            <div className="w-16 flex items-center">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={condition.percentage}
                                onChange={(e) => updateAdvancedCondition(condition.id, 'percentage', e.target.value)}
                                className="w-12 p-1 border border-gray-300 rounded-md text-center"
                                inputmode="numeric"
                              />
                              <span className="ml-1">%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    <strong>Como funciona:</strong> As condições são avaliadas em ordem. 
                    A primeira condição que corresponder será aplicada.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setShowAddRule(false)}
              className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveRule}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Salvar Regra
            </button>
          </div>
        </div>
      )}

      {/* Lista de regras */}
      <div className="px-4 py-5 sm:p-6">
        {rules.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>Nenhuma regra de comissão configurada</p>
            <p className="text-sm mt-2">Clique em "+ Nova Regra" para começar</p>
          </div>
        ) : (
          <div className="overflow-hidden bg-white rounded-md border border-gray-200">
            {rules.map((rule) => (
              <div 
                key={rule.id} 
                className={`p-4 border-b border-gray-200 last:border-0 ${!rule.active && 'bg-gray-50'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 flex items-center">
                      {rule.name}
                      {!rule.active && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800">
                          Inativa
                        </span>
                      )}
                    </h4>
                    <div className="text-sm text-gray-500 mt-1">
                      {rule.type === 'simple' && (
                        <span>Comissão fixa de {rule.commissionPercentage}%</span>
                      )}
                      {rule.type === 'service' && (
                        <span>Comissão variável por serviço</span>
                      )}
                      {rule.type === 'advanced' && (
                        <span>Regra avançada com {rule.advancedConditions?.length || 0} condições</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {rule.professionalId === 'all' ? (
                        <span>Aplica-se a todos os profissionais</span>
                      ) : (
                        <span>
                          Profissional: {professionals.find(p => p.id === rule.professionalId)?.nome || 'Não encontrado'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => toggleRuleStatus(rule.id, rule.active)}
                      className={`mr-2 px-3 py-1 rounded-md text-xs font-medium ${
                        rule.active
                          ? 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                          : 'bg-green-100 hover:bg-green-200 text-green-800'
                      }`}
                    >
                      {rule.active ? 'Desativar' : 'Ativar'}
                    </button>
                    <button
                      onClick={() => deleteRule(rule.id)}
                      className="px-3 py-1 rounded-md text-xs font-medium bg-red-100 hover:bg-red-200 text-red-800"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
                
                {/* Mostrar detalhes da regra por serviço */}
                {rule.type === 'service' && rule.serviceRules && rule.serviceRules.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <details className="text-sm text-gray-600">
                      <summary className="cursor-pointer hover:text-blue-600">
                        Ver percentuais por serviço
                      </summary>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {rule.serviceRules.map(sr => (
                          <div key={sr.serviceId} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span>{sr.serviceName}</span>
                            <span className="font-medium">{sr.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
                
                {/* Mostrar detalhes da regra avançada */}
                {rule.type === 'advanced' && rule.advancedConditions && rule.advancedConditions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <details className="text-sm text-gray-600">
                      <summary className="cursor-pointer hover:text-blue-600">
                        Ver condições avançadas
                      </summary>
                      <div className="mt-2 space-y-2">
                        {rule.advancedConditions.map((condition, idx) => (
                          <div key={idx} className="p-2 bg-gray-50 rounded">
                            {renderConditionDescription(condition)}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionRules; 